import type { Lang } from "@/lib/types";

export type BookCallHourMode = "12" | "24";

/** Business-heavy IANA zones when `Intl.supportedValuesOf("timeZone")` is unavailable. */
export const BOOK_CALL_CURATED_TIME_ZONES = [
  "Europe/Stockholm",
  "Europe/London",
  "Europe/Dublin",
  "Europe/Paris",
  "Europe/Berlin",
  "Europe/Amsterdam",
  "Europe/Brussels",
  "Europe/Madrid",
  "Europe/Rome",
  "Europe/Lisbon",
  "Europe/Vienna",
  "Europe/Zurich",
  "Europe/Warsaw",
  "Europe/Prague",
  "Europe/Helsinki",
  "Europe/Oslo",
  "Europe/Copenhagen",
  "Europe/Athens",
  "Europe/Bucharest",
  "Europe/Istanbul",
  "Europe/Moscow",
  "America/New_York",
  "America/Chicago",
  "America/Denver",
  "America/Los_Angeles",
  "America/Phoenix",
  "America/Anchorage",
  "America/Toronto",
  "America/Vancouver",
  "America/Mexico_City",
  "America/Bogota",
  "America/Lima",
  "America/Santiago",
  "America/Sao_Paulo",
  "America/Buenos_Aires",
  "America/Halifax",
  "America/St_Johns",
  "America/Puerto_Rico",
  "America/Costa_Rica",
  "America/Panama",
  "Asia/Dubai",
  "Asia/Riyadh",
  "Asia/Calcutta",
  "Asia/Karachi",
  "Asia/Dhaka",
  "Asia/Bangkok",
  "Asia/Singapore",
  "Asia/Hong_Kong",
  "Asia/Shanghai",
  "Asia/Taipei",
  "Asia/Tokyo",
  "Asia/Seoul",
  "Asia/Jakarta",
  "Asia/Manila",
  "Asia/Saigon",
  "Asia/Jerusalem",
  "Asia/Qatar",
  "Asia/Muscat",
  "Asia/Bahrain",
  "Asia/Kuwait",
  "Asia/Colombo",
  "Australia/Sydney",
  "Australia/Melbourne",
  "Australia/Brisbane",
  "Australia/Perth",
  "Australia/Adelaide",
  "Pacific/Auckland",
  "Pacific/Fiji",
  "Pacific/Honolulu",
  "Pacific/Guam",
  "Africa/Cairo",
  "Africa/Johannesburg",
  "Africa/Lagos",
  "Africa/Nairobi",
  "Africa/Casablanca",
  "Africa/Accra",
  "Atlantic/Reykjavik",
  "Indian/Mauritius",
  "Etc/UTC",
] as const;

const BOOK_CALL_TIME_ZONE_REGION_LABELS: Record<string, string> = {
  Africa: "Africa",
  America: "Americas",
  Antarctica: "Antarctica",
  Arctic: "Arctic",
  Asia: "Asia",
  Atlantic: "Atlantic",
  Australia: "Australia",
  Etc: "UTC & offsets",
  Europe: "Europe",
  Indian: "Indian Ocean",
  Pacific: "Pacific",
};

export type BookCallTimeZoneOption = {
  id: string;
  label: string;
  offsetMinutes: number;
  region: string;
};

export type BookCallTimeZoneOptionGroup = {
  region: string;
  regionLabel: string;
  options: BookCallTimeZoneOption[];
};

/** BCP 47 tags used for booking UI date/time formatting. */
export function bookCallIntlLocale(lang: Lang): string {
  if (lang === "sv") return "sv-SE";
  if (lang === "no") return "nb-NO";
  if (lang === "da") return "da-DK";
  if (lang === "de") return "de-DE";
  if (lang === "nl") return "nl-NL";
  if (lang === "es") return "es-ES";
  if (lang === "fr") return "fr-FR";
  if (lang === "pt") return "pt-PT";
  if (lang === "it") return "it-IT";
  if (lang === "fi") return "fi-FI";
  return "en-GB";
}

export function prefers12HourClock(timeZone: string): boolean {
  return (
    timeZone.startsWith("America/") ||
    timeZone.startsWith("Australia/") ||
    timeZone === "Pacific/Auckland"
  );
}

export function calendarStartsOnSunday(timeZone: string): boolean {
  return (
    timeZone.startsWith("America/") ||
    [
      "Asia/Hong_Kong",
      "Asia/Manila",
      "Asia/Seoul",
      "Asia/Taipei",
      "Asia/Tokyo",
      "Pacific/Honolulu",
    ].includes(timeZone)
  );
}

export function getDatePartsInTimeZone(
  instant: Date,
  timeZone: string,
): { year: number; month: number; day: number } {
  const parts = new Intl.DateTimeFormat("en-CA", {
    timeZone,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).formatToParts(instant);
  const lookup = Object.fromEntries(parts.map((part) => [part.type, part.value]));
  return {
    year: Number(lookup.year),
    month: Number(lookup.month),
    day: Number(lookup.day),
  };
}

export function dateKeyFromParts(parts: {
  year: number;
  month: number;
  day: number;
}): string {
  return `${parts.year}-${String(parts.month).padStart(2, "0")}-${String(parts.day).padStart(2, "0")}`;
}

export function dateKeyInTimeZone(instant: Date, timeZone: string): string {
  return dateKeyFromParts(getDatePartsInTimeZone(instant, timeZone));
}

/** Calendar-only dates from API keys (YYYY-MM-DD), safe for Intl with a timeZone. */
export function parseCalendarDateKey(dateKey: string): Date {
  const [year, month, day] = dateKey.split("-").map(Number);
  return new Date(Date.UTC(year, month - 1, day, 12));
}

export function isValidBookCallTimeZone(timeZone: string): boolean {
  try {
    Intl.DateTimeFormat("en-US", { timeZone }).format(new Date());
    return true;
  } catch {
    return false;
  }
}

export function parseGmtOffsetToMinutes(value: string): number {
  const normalized = value.trim().toUpperCase();
  if (!normalized || normalized === "GMT" || normalized === "UTC") return 0;
  const match = normalized.match(/^(?:GMT|UTC)([+-])(\d{1,2})(?::(\d{2}))?$/);
  if (!match) return 0;
  const sign = match[1] === "-" ? -1 : 1;
  const hours = Number(match[2]);
  const minutes = Number(match[3] ?? "0");
  return sign * (hours * 60 + minutes);
}

export function getBookCallTimeZoneOffsetMinutes(
  timeZone: string,
  reference: Date = new Date(),
): number {
  try {
    const offset = new Intl.DateTimeFormat("en-GB", {
      timeZone,
      timeZoneName: "longOffset",
    })
      .formatToParts(reference)
      .find((part) => part.type === "timeZoneName")?.value;
    return parseGmtOffsetToMinutes(offset ?? "");
  } catch {
    return 0;
  }
}

export function bookCallTimeZoneRegion(timeZone: string): string {
  const [region] = timeZone.split("/");
  return region || "Other";
}

export function bookCallTimeZoneRegionLabel(region: string): string {
  return BOOK_CALL_TIME_ZONE_REGION_LABELS[region] ?? region.replaceAll("_", " ");
}

export function getBookCallTimeZoneIds(): readonly string[] {
  try {
    const intl = Intl as typeof Intl & {
      supportedValuesOf?: (key: string) => string[];
    };
    const values = intl.supportedValuesOf?.("timeZone");
    if (values?.length) return values;
  } catch {
    // Older runtimes may not implement supportedValuesOf.
  }
  return BOOK_CALL_CURATED_TIME_ZONES.filter(isValidBookCallTimeZone);
}

export function listBookCallTimeZoneOptions(
  locale: string,
  extraTimeZone?: string,
  reference: Date = new Date(),
): BookCallTimeZoneOption[] {
  const ids = new Set(getBookCallTimeZoneIds());
  if (extraTimeZone && isValidBookCallTimeZone(extraTimeZone)) {
    ids.add(extraTimeZone);
  }

  const options = [...ids].map((id) => ({
    id,
    label: formatBookCallTimeZoneOption(id, locale, reference),
    offsetMinutes: getBookCallTimeZoneOffsetMinutes(id, reference),
    region: bookCallTimeZoneRegion(id),
  }));

  options.sort((left, right) => {
    if (left.offsetMinutes !== right.offsetMinutes) {
      return left.offsetMinutes - right.offsetMinutes;
    }
    return left.label.localeCompare(right.label, locale, { sensitivity: "base" });
  });

  return options;
}

export function groupBookCallTimeZoneOptions(
  options: BookCallTimeZoneOption[],
): BookCallTimeZoneOptionGroup[] {
  const groups = new Map<string, BookCallTimeZoneOption[]>();
  for (const option of options) {
    const bucket = groups.get(option.region) ?? [];
    bucket.push(option);
    groups.set(option.region, bucket);
  }

  return [...groups.entries()]
    .map(([region, regionOptions]) => ({
      region,
      regionLabel: bookCallTimeZoneRegionLabel(region),
      options: regionOptions,
    }))
    .sort((left, right) => {
      const leftOffset = left.options[0]?.offsetMinutes ?? 0;
      const rightOffset = right.options[0]?.offsetMinutes ?? 0;
      if (leftOffset !== rightOffset) return leftOffset - rightOffset;
      return left.regionLabel.localeCompare(right.regionLabel, "en", {
        sensitivity: "base",
      });
    });
}

export function filterBookCallTimeZoneOptions(
  options: BookCallTimeZoneOption[],
  query: string,
): BookCallTimeZoneOption[] {
  const normalized = query.trim().toLowerCase();
  if (!normalized) return options;
  return options.filter((option) => {
    const haystack = `${option.label} ${option.id}`.toLowerCase();
    return haystack.includes(normalized);
  });
}

export function formatBookCallTimeZoneLabel(
  timeZone: string,
  locale: string,
): string {
  try {
    const display = new Intl.DisplayNames([locale], {
      type: "timeZone",
    } as unknown as Intl.DisplayNamesOptions);
    const localized = display.of(timeZone);
    if (localized) return localized;
  } catch {
    // Intl.DisplayNames may be unavailable in some runtimes.
  }
  return timeZone.replaceAll("_", " ").replace(/\//g, " · ");
}

export function formatBookCallTimeZoneOption(
  timeZone: string,
  locale: string,
  reference: Date = new Date(),
): string {
  const name = formatBookCallTimeZoneLabel(timeZone, locale);
  const offset = new Intl.DateTimeFormat(locale, {
    timeZone,
    timeZoneName: "shortOffset",
  })
    .formatToParts(reference)
    .find((part) => part.type === "timeZoneName")?.value;
  return offset ? `${name} (${offset})` : name;
}

export function formatBookCallSlotTime(
  iso: string,
  locale: string,
  timeZone: string,
  hourMode: BookCallHourMode,
): string {
  return new Intl.DateTimeFormat(locale, {
    hour: hourMode === "12" ? "numeric" : "2-digit",
    minute: "2-digit",
    hour12: hourMode === "12",
    timeZone,
  }).format(new Date(iso));
}

export function formatBookCallDateTime(
  iso: string,
  locale: string,
  timeZone: string,
  hourMode: BookCallHourMode,
): string {
  return new Intl.DateTimeFormat(locale, {
    weekday: "long",
    day: "numeric",
    month: "long",
    hour: hourMode === "12" ? "numeric" : "2-digit",
    minute: "2-digit",
    hour12: hourMode === "12",
    timeZone,
  }).format(new Date(iso));
}

export function formatBookCallWeekdayDate(
  dateKey: string,
  locale: string,
  timeZone: string,
): string {
  return new Intl.DateTimeFormat(locale, {
    weekday: "long",
    day: "numeric",
    month: "long",
    timeZone,
  }).format(parseCalendarDateKey(dateKey));
}

export function formatBookCallMonthYear(
  year: number,
  monthIndex: number,
  locale: string,
  timeZone: string,
): string {
  const anchor = new Date(Date.UTC(year, monthIndex, 15, 12));
  return new Intl.DateTimeFormat(locale, {
    month: "long",
    year: "numeric",
    timeZone,
  }).format(anchor);
}

export function formatBookCallWeekdayShort(
  locale: string,
  timeZone: string,
  weekStartsOnSunday: boolean,
): string[] {
  return Array.from({ length: 7 }, (_, index) =>
    new Intl.DateTimeFormat(locale, { weekday: "short", timeZone }).format(
      new Date(
        Date.UTC(2026, 0, 4 + index + (weekStartsOnSunday ? 0 : 1), 12),
      ),
    ),
  );
}

export function formatBookCallFullDate(
  dateKey: string,
  locale: string,
  timeZone: string,
): string {
  return new Intl.DateTimeFormat(locale, {
    dateStyle: "full",
    timeZone,
  }).format(parseCalendarDateKey(dateKey));
}

export function monthGrid(
  year: number,
  monthIndex: number,
  weekStartsOnSunday: boolean,
): Array<{ day: number } | null> {
  const first = new Date(year, monthIndex, 1, 12);
  const firstWeekday = weekStartsOnSunday ? 0 : 1;
  const leadingDays = (first.getDay() - firstWeekday + 7) % 7;
  const dayCount = new Date(year, monthIndex + 1, 0).getDate();
  const cells: Array<{ day: number } | null> = Array.from(
    { length: leadingDays },
    () => null,
  );
  for (let day = 1; day <= dayCount; day += 1) {
    cells.push({ day });
  }
  while (cells.length % 7 !== 0) cells.push(null);
  return cells;
}
